import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Group as GroupIcon,
  MarkEmailUnread as UnreadIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useChat } from '../../../contexts/ChatContext';
import { useUpload } from '../../../contexts/UploadContext';
import { chatAPI } from '../../../services/api';
import { chatWebSocket } from '../../../services/chatWebSocket';
import { Conversation, ChatMessage } from '../../../types/chat';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import ConversationList from '../../../components/chat/ConversationList';
import MessageThread from '../../../components/chat/MessageThread';
import NewConversationDialog from '../../../components/chat/NewConversationDialog';
import GroupConversationDialog from '../../../components/chat/GroupConversationDialog';
import GroupSettingsDialog from '../../../components/chat/GroupSettingsDialog';
import MessageSearch from '../../../components/chat/MessageSearch';

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

interface MessagerieOverviewProps {
  filter?: 'all' | 'groups' | 'unread' | 'starred' | 'archived' | 'deleted' | 'search';
}

const MessagerieOverview: React.FC<MessagerieOverviewProps> = ({ filter = 'all' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { alertState, showError, hideAlert } = useAlert();

  const { user } = useAuth();
  const {
    state,
    loadConversations,
    setActiveConversation,
    sendMessage,
    sendTypingIndicator,
    deleteMessage,
    markMessagesAsRead,
  } = useChat();
  const { uploadFile, getConversationUploads, cancelUpload } = useUpload();

  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [groupConversationOpen, setGroupConversationOpen] = useState(false);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [deletedConversations, setDeletedConversations] = useState<Conversation[]>([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Helper to enrich conversation with other_participant
  const enrichConversation = (conversation: Conversation): Conversation => {
    const otherParticipant = conversation.participants.find(
      p => p.user_id !== user?.id
    );
    return {
      ...conversation,
      other_participant: otherParticipant?.user,
    };
  };

  // Load deleted conversations when on deleted filter
  const loadDeletedConversations = async () => {
    setLoadingDeleted(true);
    try {
      const response = await chatAPI.getDeletedConversations();
      // Enrich with other_participant
      const enriched = response.conversations.map(enrichConversation);
      setDeletedConversations(enriched);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoadingDeleted(false);
    }
  };

  // Load conversations on mount
  useEffect(() => {
    if (filter === 'deleted') {
      loadDeletedConversations();
    } else {
      loadConversations();
    }
  }, [loadConversations, filter]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (state.activeConversationId) {
      loadMessages(state.activeConversationId);
    } else {
      setMessages([]);
    }
  }, [state.activeConversationId]);

  // Update messages when new message received
  useEffect(() => {
    if (state.activeConversationId) {
      const conversation = state.conversations.find(
        (c) => c.id === state.activeConversationId
      );
      if (conversation?.last_message) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === conversation.last_message!.id);
          if (!exists) {
            return [...prev, conversation.last_message!];
          }
          return prev;
        });
      }
    }
  }, [state.conversations, state.activeConversationId]);

  // Subscribe to WebSocket for real-time messages and read receipts
  useEffect(() => {
    const unsubscribe = chatWebSocket.onMessage((message) => {
      if (message.type === 'new_message') {
        const newMsg = message.data as ChatMessage;

        // Add new message to local state if it's for the active conversation
        if (newMsg.conversation_id === state.activeConversationId) {
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.some((m) => m.id === newMsg.id)) {
              return prev;
            }
            return [...prev, newMsg];
          });
        }
      }

      // Handle read receipt - update is_read status for messages I sent
      if (message.type === 'message_read') {
        const { conversation_id, read_at } = message.data;

        // Update is_read for messages I sent in this conversation up to read_at time
        if (conversation_id === state.activeConversationId && read_at && user) {
          setMessages((prev) =>
            prev.map((msg) => {
              // Only mark MY messages as read (messages I sent to others)
              if (msg.sender_id === user.id && new Date(msg.created_at) <= new Date(read_at)) {
                return { ...msg, is_read: true };
              }
              return msg;
            })
          );
        }
      }

      // Handle message deleted - mark message as deleted in real-time
      if (message.type === 'message_deleted') {
        const { message_id, conversation_id } = message.data;

        if (conversation_id === state.activeConversationId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === message_id
                ? { ...msg, is_deleted: true, content: null, attachments: [] }
                : msg
            )
          );
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [state.activeConversationId]);

  const loadMessages = async (conversationId: number) => {
    setLoadingMessages(true);
    try {
      const response = await chatAPI.getMessages(conversationId);
      setMessages(response.messages);

      // Mark unread messages from others as read
      const unreadMessageIds = response.messages
        .filter((m: ChatMessage) => !m.is_read && m.sender_id !== user?.id)
        .map((m: ChatMessage) => m.id);

      if (unreadMessageIds.length > 0) {
        markMessagesAsRead(unreadMessageIds);
      }
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setActiveConversation(conversation.id);
  };

  const handleNewConversation = () => {
    setNewConversationOpen(true);
  };

  const handleNewGroupConversation = () => {
    setGroupConversationOpen(true);
  };

  const handleCreateGroup = async (groupName: string, participantIds: number[]) => {
    try {
      const response = await chatAPI.createGroupConversation({ group_name: groupName, participant_ids: participantIds });
      await loadConversations();
      setActiveConversation(response.id);
      setGroupConversationOpen(false);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleOpenGroupSettings = () => {
    setGroupSettingsOpen(true);
  };

  const handleLeaveGroup = async () => {
    if (!state.activeConversationId || !user) return;

    try {
      await chatAPI.removeGroupMember(state.activeConversationId, user.id);
      setActiveConversation(null);
      setGroupSettingsOpen(false);
      await loadConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleConversationUpdate = (updatedConversation: Conversation) => {
    // Reload conversations to reflect the update
    loadConversations();
  };

  const handleToggleStar = async (conversationId: number) => {
    try {
      await chatAPI.toggleStar(conversationId);
      await loadConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleToggleArchive = async (conversationId: number) => {
    try {
      await chatAPI.toggleArchive(conversationId);
      await loadConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDeleteConversation = async (conversationId: number) => {
    try {
      await chatAPI.deleteConversation(conversationId);
      // If deleted conversation is active, clear it
      if (state.activeConversationId === conversationId) {
        setActiveConversation(null);
      }
      await loadConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleRestoreConversation = async (conversationId: number) => {
    try {
      await chatAPI.restoreConversation(conversationId);
      // If restored conversation is active, clear it
      if (state.activeConversationId === conversationId) {
        setActiveConversation(null);
      }
      await loadDeletedConversations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleSelectUser = async (userId: number) => {
    try {
      const response = await chatAPI.createConversation({ participant_id: userId });
      await loadConversations();
      setActiveConversation(response.id);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleSendMessage = async (content: string, files: File[]) => {
    if (!state.activeConversationId) return;

    // If there are files, upload them with progress tracking (non-blocking)
    if (files.length > 0) {
      // Start all uploads in parallel
      const uploadPromises = files.map(file =>
        uploadFile(state.activeConversationId!, file)
      );

      // Wait for all uploads to complete
      try {
        const results = await Promise.all(uploadPromises);
        const attachmentIds = results.filter((id): id is number => id !== null);

        if (attachmentIds.length > 0) {
          // Send message with attachments
          const newMessage = await sendMessage(content || '', attachmentIds);
          if (newMessage) {
            setMessages((prev) => [...prev, newMessage]);
          }
        }
      } catch (error: any) {
        showError(`Échec de l'envoi du fichier: ${extractErrorMessage(error)}`);
      }
    } else if (content.trim()) {
      // Send text-only message
      const newMessage = await sendMessage(content, undefined);
      if (newMessage) {
        setMessages((prev) => [...prev, newMessage]);
      }
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    const success = await deleteMessage(messageId);
    if (success) {
      // Mark message as deleted instead of removing it (WhatsApp style)
      setMessages((prev) => prev.map((m) =>
        m.id === messageId
          ? { ...m, is_deleted: true, content: null, attachments: [] }
          : m
      ));
    }
  };

  const handleTyping = useCallback(() => {
    sendTypingIndicator();
  }, [sendTypingIndicator]);

  const handleBack = () => {
    setActiveConversation(null);
  };

  // For deleted filter, use deletedConversations; for others, use state.conversations
  const activeConversation = filter === 'deleted'
    ? deletedConversations.find((c) => c.id === state.activeConversationId)
    : state.conversations.find((c) => c.id === state.activeConversationId);

  // Filter conversations based on the filter prop
  const filteredConversations = (() => {
    switch (filter) {
      case 'groups':
        return state.conversations.filter((c) => c.is_group);
      case 'unread':
        return state.conversations.filter((c) => c.unread_count > 0);
      case 'starred':
        return state.conversations.filter((c) => c.is_starred);
      case 'archived':
        return state.conversations.filter((c) => c.is_archived);
      case 'deleted':
        return deletedConversations;
      default:
        // 'all' - show non-archived conversations
        return state.conversations.filter((c) => !c.is_archived);
    }
  })();

  // Get uploads for current conversation
  const currentUploads = state.activeConversationId
    ? getConversationUploads(state.activeConversationId)
    : [];

  // Page title and icon based on filter
  const getPageConfig = () => {
    switch (filter) {
      case 'groups':
        return { title: 'Groupes', icon: GroupIcon };
      case 'unread':
        return { title: 'Non lus', icon: UnreadIcon };
      case 'starred':
        return { title: 'Favoris', icon: StarIcon };
      case 'archived':
        return { title: 'Archivés', icon: ArchiveIcon };
      case 'deleted':
        return { title: 'Supprimés', icon: DeleteIcon };
      case 'search':
        return { title: 'Rechercher', icon: SearchIcon };
      default:
        return { title: 'Messagerie', icon: ChatIcon };
    }
  };
  const { title: pageTitle, icon: PageIcon } = getPageConfig();

  // Handle search result selection
  const handleSearchResultSelect = async (conversationId: number, messageId: number) => {
    setActiveConversation(conversationId);
    // TODO: Could scroll to the specific message in the thread
  };

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: OUTLOOK_BLUE,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <PageIcon />
          {pageTitle}
        </Typography>
      </Box>

      {/* Chat container */}
      <Paper
        elevation={2}
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        {filter === 'search' ? (
          // Search view
          isMobile ? (
            state.activeConversationId && activeConversation ? (
              <MessageThread
                conversation={activeConversation}
                messages={messages}
                currentUserId={user?.id || 0}
                typingUsers={state.typingUsers}
                loading={loadingMessages}
                uploads={currentUploads}
                onSendMessage={handleSendMessage}
                onDeleteMessage={handleDeleteMessage}
                onTyping={handleTyping}
                onBack={handleBack}
                onCancelUpload={cancelUpload}
                showBackButton={true}
                onOpenGroupSettings={handleOpenGroupSettings}
                onLeaveGroup={handleLeaveGroup}
                onToggleStar={handleToggleStar}
                onToggleArchive={handleToggleArchive}
                onDeleteConversation={handleDeleteConversation}
              />
            ) : (
              <MessageSearch onSelectResult={handleSearchResultSelect} />
            )
          ) : (
            <>
              <Box sx={{ width: 400, flexShrink: 0 }}>
                <MessageSearch onSelectResult={handleSearchResultSelect} />
              </Box>
              <Box sx={{ flex: 1, borderLeft: '1px solid #e0e0e0' }}>
                {state.activeConversationId && activeConversation ? (
                  <MessageThread
                    conversation={activeConversation}
                    messages={messages}
                    currentUserId={user?.id || 0}
                    typingUsers={state.typingUsers}
                    loading={loadingMessages}
                    uploads={currentUploads}
                    onSendMessage={handleSendMessage}
                    onDeleteMessage={handleDeleteMessage}
                    onTyping={handleTyping}
                    onCancelUpload={cancelUpload}
                    onOpenGroupSettings={handleOpenGroupSettings}
                    onLeaveGroup={handleLeaveGroup}
                    onToggleStar={handleToggleStar}
                    onToggleArchive={handleToggleArchive}
                    onDeleteConversation={handleDeleteConversation}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#666' }}>
                      Recherchez et sélectionnez un message
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                      pour voir la conversation
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )
        ) : isMobile ? (
          // Mobile: show either list or thread
          state.activeConversationId && activeConversation ? (
            <MessageThread
              conversation={activeConversation}
              messages={messages}
              currentUserId={user?.id || 0}
              typingUsers={state.typingUsers}
              loading={loadingMessages}
              uploads={currentUploads}
              onSendMessage={handleSendMessage}
              onDeleteMessage={handleDeleteMessage}
              onTyping={handleTyping}
              onBack={handleBack}
              onCancelUpload={cancelUpload}
              showBackButton={true}
              onOpenGroupSettings={filter === 'deleted' ? undefined : handleOpenGroupSettings}
              onLeaveGroup={filter === 'deleted' ? undefined : handleLeaveGroup}
              onToggleStar={filter === 'deleted' ? undefined : handleToggleStar}
              onToggleArchive={filter === 'deleted' ? undefined : handleToggleArchive}
              onDeleteConversation={filter === 'deleted' ? undefined : handleDeleteConversation}
              onRestoreConversation={filter === 'deleted' ? handleRestoreConversation : undefined}
            />
          ) : (
            <ConversationList
              conversations={filteredConversations}
              selectedId={state.activeConversationId || undefined}
              loading={filter === 'deleted' ? loadingDeleted : state.loading}
              onSelect={handleSelectConversation}
              onNewConversation={filter === 'groups' || filter === 'archived' || filter === 'deleted' ? undefined : handleNewConversation}
              onToggleStar={filter === 'deleted' ? undefined : handleToggleStar}
              onToggleArchive={filter === 'deleted' ? undefined : handleToggleArchive}
              onDelete={filter === 'deleted' ? undefined : handleDeleteConversation}
              onRestore={filter === 'deleted' ? handleRestoreConversation : undefined}
              onNewGroupConversation={filter === 'deleted' ? undefined : handleNewGroupConversation}
              onRefresh={filter === 'deleted' ? loadDeletedConversations : loadConversations}
            />
          )
        ) : (
          // Desktop: side-by-side layout
          <>
            {/* Conversation list */}
            <Box sx={{ width: 320, flexShrink: 0 }}>
              <ConversationList
                conversations={filteredConversations}
                selectedId={state.activeConversationId || undefined}
                loading={filter === 'deleted' ? loadingDeleted : state.loading}
                onSelect={handleSelectConversation}
                onNewConversation={filter === 'groups' || filter === 'archived' || filter === 'deleted' ? undefined : handleNewConversation}
                onNewGroupConversation={filter === 'deleted' ? undefined : handleNewGroupConversation}
                onRefresh={filter === 'deleted' ? loadDeletedConversations : loadConversations}
                onToggleStar={filter === 'deleted' ? undefined : handleToggleStar}
                onToggleArchive={filter === 'deleted' ? undefined : handleToggleArchive}
                onDelete={filter === 'deleted' ? undefined : handleDeleteConversation}
                onRestore={filter === 'deleted' ? handleRestoreConversation : undefined}
              />
            </Box>

            {/* Message thread */}
            <Box sx={{ flex: 1, borderLeft: '1px solid #e0e0e0' }}>
              {state.activeConversationId && activeConversation ? (
                <MessageThread
                  conversation={activeConversation}
                  messages={messages}
                  currentUserId={user?.id || 0}
                  typingUsers={state.typingUsers}
                  loading={loadingMessages}
                  uploads={currentUploads}
                  onSendMessage={handleSendMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onTyping={handleTyping}
                  onCancelUpload={cancelUpload}
                  onOpenGroupSettings={filter === 'deleted' ? undefined : handleOpenGroupSettings}
                  onLeaveGroup={filter === 'deleted' ? undefined : handleLeaveGroup}
                  onToggleStar={filter === 'deleted' ? undefined : handleToggleStar}
                  onToggleArchive={filter === 'deleted' ? undefined : handleToggleArchive}
                  onDeleteConversation={filter === 'deleted' ? undefined : handleDeleteConversation}
                  onRestoreConversation={filter === 'deleted' ? handleRestoreConversation : undefined}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <ChatIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#666' }}>
                    Sélectionnez une conversation
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                    Choisissez une conversation ou démarrez-en une nouvelle
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* New conversation dialog */}
      <NewConversationDialog
        open={newConversationOpen}
        onClose={() => setNewConversationOpen(false)}
        onSelectUser={handleSelectUser}
        currentUserId={user?.id || 0}
      />

      {/* Group conversation dialog */}
      <GroupConversationDialog
        open={groupConversationOpen}
        onClose={() => setGroupConversationOpen(false)}
        onCreateGroup={handleCreateGroup}
        currentUserId={user?.id || 0}
      />

      {/* Group settings dialog */}
      {activeConversation?.is_group && (
        <GroupSettingsDialog
          open={groupSettingsOpen}
          onClose={() => setGroupSettingsOpen(false)}
          conversation={activeConversation}
          currentUserId={user?.id || 0}
          onConversationUpdate={handleConversationUpdate}
          onLeaveGroup={handleLeaveGroup}
        />
      )}
    </Box>
  );
};

export default MessagerieOverview;
